import type React from 'react';

import type {
    AnimationData,
    AnimationImageAsset,
    AnimationTranscriptWord,
    AnimationVideoAsset,
    CharacterInputMode,
    CharacterTransformHistoryEntry,
    ContentSource,
    ImageHistoryEntry,
    Ingredient,
    IngredientSource,
    Shot,
    ShotList,
    StageId,
    StyleReferenceAsset,
    VideoHistoryEntry,
} from './migrations/types/current';
import { CURRENT_ANIMATION_DATA_VERSION } from './migrations/types/current';

export type {
    AnimationData,
    AnimationImageAsset,
    AnimationTranscriptWord,
    AnimationVideoAsset,
    CharacterInputMode,
    CharacterTransformHistoryEntry,
    ContentSource,
    ImageHistoryEntry,
    Ingredient,
    IngredientSource,
    Shot,
    ShotList,
    StageId,
    StyleReferenceAsset,
    VideoHistoryEntry,
};

export const ANIMATION_DATA_VERSION = CURRENT_ANIMATION_DATA_VERSION;

let shotIdCounter = 0;
export function generateShotId(): string {
    return `shot-${Date.now()}-${shotIdCounter++}`;
}

let historyIdCounter = 0;
export function generateHistoryId(): string {
    return `hist-${Date.now()}-${historyIdCounter++}`;
}

let ingredientIdCounter = 0;
export function generateIngredientId(): string {
    return `ing-${Date.now()}-${ingredientIdCounter++}`;
}

export function isCompleteImageEntry(
    e: ImageHistoryEntry,
): e is ImageHistoryEntry & { asset: AnimationImageAsset } {
    return !e.isGenerating && !e.errorMessage && e.asset !== null;
}

export function isCompleteVideoEntry(
    e: VideoHistoryEntry,
): e is VideoHistoryEntry & { asset: AnimationVideoAsset } {
    return !e.isGenerating && !e.errorMessage && e.asset !== null;
}

export function getRenderImageAsset(shot: Shot): AnimationImageAsset | null {
    if (!shot.renderImageEntryId) return null;
    const entry = shot.imageHistory.find((e) => e.id === shot.renderImageEntryId);
    return entry?.asset ?? null;
}

export function getRenderVideoAsset(shot: Shot): AnimationVideoAsset | null {
    if (!shot.renderVideoEntryId) return null;
    const entry = shot.videoHistory.find((e) => e.id === shot.renderVideoEntryId);
    return entry?.asset ?? null;
}

export interface DescribeStyleSnapshot {
    styleReferenceAssets: string[];
    imageStyleGuidelines: string;
    videoStyleGuidelines: string;
    selectedPresetId: string | undefined;
}

export interface GenerateScriptSnapshot {
    contentSource: ContentSource;
    targetDuration: number;
    selectedVoiceSlug: string | null;
    script: string;
}

export interface IngredientsSnapshot {
    ingredients: Array<{
        name: string;
        source: IngredientSource;
        referenceAssetUuid: string | null;
    }>;
}

export function snapshotsEqual<T>(a: T | null, b: T | null): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export interface StageConfig {
    id: StageId;
    title: string;
    getContinueEnabled: () => boolean;
    hideContinueWhenDisabled?: boolean;
    /** Tooltip text shown on the disabled Continue button. */
    getContinueDisabledTooltip?: () => string | undefined;
    /**
     * Render the Continue button as disabled with a spinner. Used while async
     * `beforeContinue` work is in flight so the user has a visible loading
     * state instead of the button vanishing.
     */
    getContinueLoading?: () => boolean;
    getSummary: () => string | undefined;
    getSnapshot: () => unknown;
    getSnapshotFromAnimationData: (data: AnimationData) => unknown;
    onEnter?: (hasChanged: boolean) => void;
    beforeContinue?: () => boolean | Promise<boolean>;
    onSkip?: () => void;
    getContinueText?: () => string;
    invalidateStageIds?: StageId[];
    renderContent: () => React.ReactNode;
    renderWrapper?: (accordion: React.ReactNode, isActive: boolean) => React.ReactNode;
}

export const STAGE_IDS = {
    DESCRIBE_STYLE: 'describe-style',
    GENERATE_SCRIPT: 'generate-script',
    INGREDIENTS: 'ingredients',
    GENERATE: 'generate',
} as const satisfies Record<string, StageId>;

export function isStageId(value: unknown): value is StageId {
    return (
        typeof value === 'string' && (Object.values(STAGE_IDS) as readonly string[]).includes(value)
    );
}

const CONTENT_SOURCE_VALUES = new Set<ContentSource>(['describe', 'paste', 'url', 'upload']);

export function isContentSource(value: unknown): value is ContentSource {
    return typeof value === 'string' && CONTENT_SOURCE_VALUES.has(value as ContentSource);
}
